/**
 * Helpers de presentación del dominio de Estadísticas avanzadas.
 *
 * Puro formato/derivación para la UI; sin lógica de negocio remota ni acceso a la
 * API (`docs/frontend_architecture.md`, Pr. 6). Replica las funciones de formato y
 * los cálculos de los KPIs del SSR (`advanced_stats_with_charts.html`): formato de
 * dinero abreviado, etiquetas de fecha por período, conteo de órdenes por estado y
 * la agregación del panel global (órdenes/entregadas/ingresos del período elegido).
 */
import { ApiError } from "@/lib/api";

import { STATS_GENERIC_ERROR } from "./constants";
import type { OrdersPeriod } from "./schemas";
import type { DailyStat, TimingRow } from "./types";

/**
 * Traduce un error de la API/red a un mensaje legible. Las estadísticas son de
 * solo lectura: no hay `error_code` de negocio que mapear, así que se usa el
 * `message` del `ApiError` (ya en español del backend) o del `Error`, con un texto
 * de respaldo. (Un actor multi-sede sin `X-Sede-Id` recibe el mensaje del backend
 * pidiendo la sede, que se muestra tal cual.)
 */
export function statsErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return STATS_GENERIC_ERROR;
}

/**
 * Formato de dinero abreviado (réplica de `fmtMoney` del SSR): miles -> "K",
 * millones -> "M", miles de millones -> "B"; por debajo, el valor con separador de
 * miles. Se usa en los ejes/etiquetas de las gráficas de ingresos.
 */
export function formatMoney(value: number): string {
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(1)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (value >= 1e3) {
    return `$${Math.round(value / 1e3)}K`;
  }
  return `$${value.toLocaleString("es-CO")}`;
}

/** Formato de dinero completo con separador de miles (réplica de `fmtMoneyFull`). */
export function formatMoneyFull(value: number): string {
  return `$${Math.round(value).toLocaleString("es-CO")}`;
}

/**
 * Etiqueta legible de la clave temporal de una gráfica (réplica de `fmtDate` del
 * SSR), según el período. El backend entrega claves ISO: `YYYY-MM-DD` (día/semana)
 * o `YYYY-MM` (mes). Se formatea sin construir `Date` (evita corrimientos de zona
 * horaria): se parten los componentes del string.
 */
export function formatPeriodLabel(key: string, period: OrdersPeriod): string {
  if (period === "month") {
    // Clave `YYYY-MM`: se muestra mes/año corto.
    const [year, month] = key.split("-");
    return `${month}/${(year ?? "").slice(2)}`;
  }
  // Clave `YYYY-MM-DD` (día o lunes de la semana): se muestra dd/mm.
  const parts = key.split("-");
  const month = parts[1] ?? "";
  const day = parts[2] ?? "";
  return `${day}/${month}`;
}

/** Etiqueta del período global del panel (réplica de `periodLabel` del SSR). */
export function periodGlobalLabel(period: OrdersPeriod): string {
  if (period === "day") {
    return "Hoy";
  }
  if (period === "week") {
    return "Esta semana";
  }
  return "Este mes";
}

/** Conteo de órdenes con un estado dado dentro del detalle de timing. */
export function countByStatus(rows: TimingRow[], status: string): number {
  return rows.filter((row) => row.status === status).length;
}

/** KPIs del estado actual de las órdenes (réplica de la dona "Estado actual"). */
export interface StatusCounts {
  pending: number;
  completed: number;
  delivered: number;
  cancelled: number;
}

/** Cuenta las órdenes por estado a partir del detalle de timing (estado actual). */
export function statusCounts(rows: TimingRow[]): StatusCounts {
  return {
    pending: countByStatus(rows, "pendiente"),
    completed: countByStatus(rows, "completado"),
    delivered: countByStatus(rows, "entregado"),
    cancelled: countByStatus(rows, "anulada"),
  };
}

/** Agregados del panel global de KPIs para el período elegido. */
export interface PeriodTotals {
  orders: number;
  delivered: number;
  revenue: number;
}

/**
 * Suma órdenes/entregadas/ingresos del período elegido a partir del desglose
 * diario, replicando la lógica del `loadKPIs` del SSR:
 *  - `day`: la fila de `today_colombia`.
 *  - `week`: las filas desde el lunes (de `today_colombia`) hasta hoy, inclusive.
 *  - `month`: las filas cuyo `YYYY-MM` coincide con el de hoy.
 *
 * Se opera sobre las claves ISO (string), sin construir `Date`, para no introducir
 * corrimientos de zona horaria respecto a la hora Colombia que ya usa el backend.
 */
export function periodTotals(
  dailyStats: DailyStat[],
  todayColombia: string | null,
  period: OrdersPeriod,
): PeriodTotals {
  const totals: PeriodTotals = { orders: 0, delivered: 0, revenue: 0 };
  if (!todayColombia) {
    return totals;
  }

  if (period === "day") {
    const row = dailyStats.find((stat) => stat.date === todayColombia);
    if (row) {
      totals.orders = row.total;
      totals.delivered = row.delivered;
      totals.revenue = row.revenue;
    }
    return totals;
  }

  if (period === "month") {
    const yearMonth = todayColombia.slice(0, 7);
    for (const stat of dailyStats) {
      if (stat.date.startsWith(yearMonth)) {
        totals.orders += stat.total;
        totals.delivered += stat.delivered;
        totals.revenue += stat.revenue;
      }
    }
    return totals;
  }

  // Semana: del lunes (de la semana de hoy) a hoy, inclusive, comparando ISO.
  const monday = mondayOf(todayColombia);
  for (const stat of dailyStats) {
    if (stat.date >= monday && stat.date <= todayColombia) {
      totals.orders += stat.total;
      totals.delivered += stat.delivered;
      totals.revenue += stat.revenue;
    }
  }
  return totals;
}

/**
 * Devuelve la fecha ISO (`YYYY-MM-DD`) del lunes de la semana que contiene a
 * `isoDate`. Calcula con `Date` en UTC sobre la medianoche del día para evitar que
 * la zona local del navegador desplace el día; el resultado vuelve a ISO.
 */
export function mondayOf(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  // getUTCDay: 0 = domingo .. 6 = sábado. La semana del SSR empieza en lunes.
  const dayOfWeek = date.getUTCDay();
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  date.setUTCDate(date.getUTCDate() - daysSinceMonday);
  return date.toISOString().slice(0, 10);
}

/** Una entrada (etiqueta, valor) para una barra de una gráfica simple. */
export interface BarDatum {
  label: string;
  value: number;
  /** Texto a mostrar como valor (ya formateado); por defecto el `value`. */
  display?: string;
}

/**
 * Ordena la serie de una gráfica por su clave temporal ascendente (más antiguo a la
 * izquierda), igual que `sortAsc` del SSR. No muta el arreglo de entrada.
 */
export function sortByPeriodAsc<T extends { period: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.period < b.period ? -1 : 1));
}
